import React, { useState, useEffect } from 'react';
import Modal from '../../common/Modal';
import devIssueService from '../../../services/devIssueService';

const EMPTY_FORM = {
  title: '',
  description: '',
  issue_type: 'bug',
  priority: 'medium',
  reported_by: '',
  assignee_id: '',
  story_points: '',
};

const IssueCreateModal = ({ isOpen, onClose, onCreate }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    if (isOpen) {
      setForm(EMPTY_FORM);
      devIssueService.getAssignableUsers()
        .then(res => setUsers(res.data || []))
        .catch(() => setUsers([]));
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSubmitting(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description || null,
        issue_type: form.issue_type,
        priority: form.priority,
        reported_by: form.reported_by || null,
        assignee_id: form.assignee_id || null,
        story_points: form.story_points !== '' ? parseInt(form.story_points, 10) : null,
      };
      const res = await devIssueService.create(payload);
      onCreate(res.data);
      onClose();
    } catch {
      // let parent handle errors
    } finally {
      setSubmitting(false);
    }
  };

  const field = (label, required, children) => (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">
        {label}{required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );

  const inputClass = "w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent";
  const selectClass = inputClass + " bg-white";

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="New Issue" maxWidth="max-w-lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        {field('Title', true,
          <input
            autoFocus
            value={form.title}
            onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            placeholder="Short, descriptive title…"
            className={inputClass}
            required
          />
        )}

        {field('Description', false,
          <textarea
            value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            placeholder="What's the issue or feature? Steps to reproduce, expected behavior, etc."
            rows={3}
            className={inputClass + " resize-none"}
          />
        )}

        <div className="grid grid-cols-2 gap-3">
          {field('Type', true,
            <select value={form.issue_type} onChange={e => setForm(f => ({ ...f, issue_type: e.target.value }))} className={selectClass}>
              <option value="bug">Bug</option>
              <option value="feature">Feature</option>
              <option value="improvement">Improvement</option>
            </select>
          )}
          {field('Priority', true,
            <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))} className={selectClass}>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          {field('Assignee', false,
            <select value={form.assignee_id} onChange={e => setForm(f => ({ ...f, assignee_id: e.target.value }))} className={selectClass}>
              <option value="">Unassigned</option>
              {users.map(u => (
                <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>
              ))}
            </select>
          )}
          {field('Story Points', false,
            <input
              type="number"
              min="0"
              max="100"
              value={form.story_points}
              onChange={e => setForm(f => ({ ...f, story_points: e.target.value }))}
              placeholder="e.g. 3"
              className={inputClass}
            />
          )}
        </div>

        {field('Reported By', false,
          <input
            value={form.reported_by}
            onChange={e => setForm(f => ({ ...f, reported_by: e.target.value }))}
            placeholder="Customer name, email, or 'internal'"
            className={inputClass}
          />
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!form.title.trim() || submitting}
            className="px-4 py-2 text-sm text-white bg-yellow-500 rounded-lg hover:bg-yellow-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors font-medium"
          >
            {submitting ? 'Creating…' : 'Create Issue'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default IssueCreateModal;
